import type {Direction} from '@primer/behaviors'
import {type FocusZoneHookSettings, useFocusZone} from '@primer/react'
import {useCallback, useEffect, useRef} from 'react'

import {MARKER_NAV_KEYS} from '../constants/navigation-keys'
import type {GenericMarkerNavEl} from '../helpers/marker-navigator'
import {MarkerNavigator} from '../helpers/marker-navigator'
import type {DiffAnnotation, Thread} from '../conversation-types'

/**
 * Hook that provides keyboard navigation functionality for inline markers
 *
 * Uses arrow keys to navigate between markers
 */
export function useMarkerNavigation({
  containerRef,
  markers,
  disabled = false,
  focusInStrategy = 'previous',
}: {
  containerRef: React.RefObject<HTMLElement | null>
  markers: Array<Thread | GenericMarkerNavEl | DiffAnnotation>
  // onMarkerSelected: (markerId: string) => void
  disabled?: boolean
  focusInStrategy?: FocusZoneHookSettings['focusInStrategy']
}) {
  const prevMarkerNavigator = useRef<MarkerNavigator | undefined>(undefined)
  const markerNavigator = useRef<MarkerNavigator | undefined>(undefined)

  // Create a new marker navigator or use the previous one's state if available
  // Store the current navigator for the next render
  // Needs to be in a effect since MarkerNavigator queries the DOM
  useEffect(() => {
    markerNavigator.current = new MarkerNavigator(markers, prevMarkerNavigator.current?.focusedMarker?.id || undefined)
    prevMarkerNavigator.current = markerNavigator.current
  }, [markers])

  // Handle keyboard navigation between markers, comments, and elements within.
  const getNextFocusable = useCallback(
    (_direction: Direction, from: Element | undefined, event: KeyboardEvent) => {
      const current = from as HTMLElement

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const key = event.key

      const isNotMarkerOrMarkerItem =
        !isMarker(current) && !isMarkerItem(current) && !isThreadReply(current) && !isNewComment(current)

      // Handle when the currently focused element is an element within marker or comment.
      if (
        isNotMarkerOrMarkerItem &&
        (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowRight' || key === 'ArrowLeft')
      ) {
        return markerItemEl(current) || markerEl(current) || undefined
      }

      if (key === 'ArrowUp' || key === 'ArrowDown') {
        // Handle marker element
        if (markerNavigator.current && isMarker(current)) {
          const nextMarker = markerNavigator.current.moveToNextMarker(key, markerEl(current))
          if (nextMarker) {
            return document.querySelector<HTMLElement>(`[data-marker-id="${nextMarker.id}"]`) || undefined
          }
          return current
        }

        // Handle new comment element
        if (markerNavigator.current && isNewComment(current)) {
          const nextMarker = markerNavigator.current.moveToNextMarker(key, newCommentEl(current))
          if (nextMarker) {
            return document.querySelector<HTMLElement>(`[data-marker-id="${nextMarker.id}"]`) || undefined
          }
          return current
        }

        // Handle thread reply element
        if (markerNavigator.current && isThreadReply(current)) {
          const nextMarkerItem = markerNavigator.current.moveToNextMarkerItem(key, threadReplyEl(current))
          if (nextMarkerItem) {
            return (
              document.querySelector<HTMLElement>(`[data-marker-navigation-comment-id="${nextMarkerItem.id}"]`) ||
              undefined
            )
          }
          return current
        }

        // Handle marker item element
        if (markerNavigator.current && isMarkerItem(current)) {
          const nextMarkerItem = markerNavigator.current.moveToNextMarkerItem(key, current)
          if (nextMarkerItem) {
            return (
              document.querySelector<HTMLElement>(`[data-marker-navigation-comment-id="${nextMarkerItem.id}"]`) ||
              undefined
            )
          }
          return current
        }

        return current
      }

      if (key === 'ArrowRight') {
        if (isMarkerItem(current)) return markerItemEl(current)

        if (isMarker(current)) {
          return current?.querySelector<HTMLElement>('[data-first-thread-comment="true"]') || undefined
        }

        return current
      }

      if (key === 'ArrowLeft') {
        if (isMarker(current)) return markerEl(current)
        if (isNewComment(current)) return newCommentEl(current)
        if (isThreadReply(current)) return threadReplyEl(current)

        if (isMarkerItem(current)) {
          return current?.closest<HTMLElement>('[data-marker-id]') || undefined
        }

        return current
      }

      return current ?? undefined
    },
    [markerNavigator],
  )

  // Set up focus zone for keyboard navigation
  useFocusZone(
    {
      containerRef,
      bindKeys: MARKER_NAV_KEYS,
      getNextFocusable,
      focusableElementFilter: element => {
        // If a focus zone is disabled, the focusableElementFilter still runs, so we need to guard against making elements focusable that aren't actually focusable when disabled.
        if (disabled) {
          return false
        }

        if (element.hasAttribute('data-marker-id') && element.hasAttribute('data-marker-navigation-comment-id')) {
          return true
        }

        return false
      },

      focusInStrategy,
      disabled,
    },
    [getNextFocusable, disabled],
  )

  return {markerNavigator}
}

// Helper functions to determine if the passed `el` arg is a specific element or a decendent of a specific element
function isNewComment(el: HTMLElement | undefined): boolean {
  return !!newCommentEl(el)
}

function isThreadReply(el: HTMLElement | undefined): boolean {
  return !!threadReplyEl(el)
}

function isMarker(el: HTMLElement | undefined): boolean {
  return !!el?.hasAttribute('data-marker-id')
}

function isMarkerItem(el: HTMLElement | undefined): boolean {
  return !!el?.hasAttribute('data-marker-navigation-comment-id')
}

// Helper functions to return the expected element from the passed `el arg
function newCommentEl(el: HTMLElement | undefined): HTMLElement | undefined {
  return el?.closest('[data-marker-navigation-new-thread]') ?? undefined
}

function markerEl(el: HTMLElement | undefined): HTMLElement | undefined {
  return el?.closest('[data-marker-id]') ?? undefined
}

function markerItemEl(el: HTMLElement | undefined): HTMLElement | undefined {
  return el?.closest('[data-marker-navigation-comment-id]') ?? undefined
}

function threadReplyEl(el: HTMLElement | undefined): HTMLElement | undefined {
  return el?.closest('[data-marker-navigation-thread-reply]') ?? undefined
}
